import React, { useEffect, useState, useCallback, useContext } from 'react';
import OtpInput from './Otpinput';
import { useLocation, useNavigate } from 'react-router-dom';
import useCountdownTimer from '../../hooks/useCountdownTimer';
import { makeGetCall, makePatchCall, makePostCall } from '../../effects/requests';
import { endPoints } from '../../utils/endpoints';
import { emptyFunction, getRoute, showToast } from '../../utils';
import { cookieService } from '../../services';
import { cookieServiceParam } from '../../utils/constants';
import { setToken } from '../../global/FCM/FCM';
import { MyContext } from '../../context';
import { PERMISSION_TYPE, ROLE_TYPE } from '../../types';

const DELAY_SECONDS = 60;

const MfaVerification = () => {

    const navigate = useNavigate();

    const { updateUserData } = useContext(MyContext)

    const location = useLocation();
    const logonData = location?.state?.loginRes
    const isProductionOwner = location?.state?.isProductionOwner

    const [secondsLeft, restartTimer] = useCountdownTimer({ durationSeconds: DELAY_SECONDS })
    const [verificationToken, setVerificationToken] = useState<string | null>(null);
    const [code, setCode] = useState('');
    const [error, setError] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);

    const handleChange = (next: any) => {
        setCode(next);
        if (error) setError(false);
    };

    const runVerify = useCallback(async (fullCode: any) => {
        setIsVerifying(true);
        setError(false);
        try {
            const res = await makePostCall(endPoints.TWO_FACTOR_AUTH, {
                otp: fullCode,
                token: verificationToken,
                email: logonData?.data?.email
            })
            if (res.status?.code == 200) {
                handleNavigate(res)
            } else {
                setError(true);
            }
        } catch {
            setError(true);
        } finally {
            setIsVerifying(false);
        }
    }, [code, verificationToken]);

    const handleVerifyClick = () => {
        if (code?.length < 6) {
            showToast('Invalid otp, Otp must be 6 characters.', 'error')
            return
        }
        if (code.length === 6) runVerify(code);
    };

    const userNavigate = (res: any) => {
        if (Array.isArray(res?.data?.crew_member_profile?.company_department) && res?.data?.crew_member_profile?.company_department?.length > 0) {
            res?.data?.crew_member_profile?.company_department?.map((item: any) => {
                item.label = item?.dep;
                item.value = item?.dep;
            })
        }
        updateUserData(res);
        let routeUrl = null;
        if (res?.data?.user_type === ROLE_TYPE.TYPE_CREW && res?.data?.account_completed) {
            if (!isProductionOwner && res?.data?.permission_type === PERMISSION_TYPE.TYPE_CREW_MEMBER) {
                routeUrl = '/schedule';
            } else if (res?.data?.permission_type === PERMISSION_TYPE.TYPE_CREW_MEMBER && isProductionOwner) {
                makePatchCall(endPoints.PROFILE, { permission_type: PERMISSION_TYPE.TYPE_PROD_OWNER })
                    .then((apiRes) => {
                        routeUrl = '/manage-profile/owner/add/1'
                        navigate(routeUrl)
                        return;
                    })
                    .catch((err) => { })
            } else if (res?.data?.permission_type === PERMISSION_TYPE.TYPE_PROD_OWNER) {
                routeUrl = getRoute(res.data);
            } else {
                routeUrl = '/schedule';
            }
        } else {
            routeUrl = getRoute(res.data);
        }
        navigate(routeUrl as never);
    }

    const regDeviceToken = async () => {
        const params: any = { device_type: 'WEB' }
        if (cookieService.getCookie(cookieServiceParam.browserToken)) {
            params.token = cookieService.getCookie(cookieServiceParam.browserToken)
        } else {
            const fcmToken = await setToken(emptyFunction);
            params.token = fcmToken
        }
        if (!params?.token) return
        makePostCall(endPoints.DEVICE_TOKEN, params).then(emptyFunction).catch(emptyFunction)
    }

    const handleNavigate = (mfaRes: any) => {
        cookieService.saveCookie(cookieServiceParam.token, logonData?.token?.access || "");
        cookieService.saveCookie(cookieServiceParam.userType, logonData?.data?.user_type)
        cookieService.saveCookie(cookieServiceParam.account_completed, logonData?.data?.account_completed);
        cookieService.saveCookie(cookieServiceParam.subscription_id, logonData?.data?.subscription_id)
        regDeviceToken()
        userNavigate(logonData)
    }

    const handleResend = () => {
        if (secondsLeft) {
            return
        }
        sendOtpToEmail(true);
    }

    const sendOtpToEmail = (shouldReStart?: boolean) => {
        const apiUrl = `${endPoints.TWO_FACTOR_AUTH}?email=${logonData?.data?.email}`
        makeGetCall(apiUrl).then((res: any) => {
            setVerificationToken(res?.data?.token || null);
            if (shouldReStart) {
                setCode('')
                restartTimer()
            }
        }).catch(emptyFunction)
    }

    useEffect(() => sendOtpToEmail(false), []);

    const masked = (phone = 'info@sixthdegree.io') => phone?.replace(/^(.{3}).+(.{3})$/, "$1******$2");

    return (
        <div className="mfa-screen">
            <div className="mfa-card">
                <h3 className="mfa-card__title">Authenticate Your Account</h3>
                <p className="mfa-card__subtitle">
                    Protecting your account is our top priority. Please confirm your account by entering the authorization code sent to{masked(logonData?.data?.email) ? <> <strong>{masked(logonData?.data?.email)}</strong></> : ''}
                </p>

                <div className="mfa-card__otp">
                    <OtpInput
                        length={6}
                        value={code}
                        onChange={handleChange}
                        onComplete={runVerify}
                        error={error}
                        disabled={isVerifying}
                    />
                </div>

                {error && <p className="mfa-error">That code didn't work. Please try again.</p>}

                <button
                    className="mfa-verify-btn"
                    onClick={handleVerifyClick}
                    disabled={code.length !== 6 || isVerifying}
                >
                    {isVerifying ? 'Verifying…' : 'Verify'}
                </button>

                <p className="mfa-resend">
                    Didn’t receive a code?{' '}
                    {secondsLeft > 0 ? (
                        <span className="mfa-resend__timer">Resend in {secondsLeft}</span>
                    ) : (
                        <button className="mfa-resend__link" onClick={handleResend}>Resend code</button>
                    )}
                </p>
            </div>
        </div>
    );
};

export default MfaVerification;